import { useQuery } from '@apollo/client';
import { Columns, Stack, Notice, Text, Hidden } from 'braid-design-system';
import queryString from 'query-string';
import React, { Fragment } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Routes from 'src/config/routes';
import { isFreemiumExperiment } from 'src/data/listing-products/products';
import { hasProductInfo } from 'src/data/listing-products/shared';
import {
  GetListingProductsForPackagesPageDocument,
  ListingProductCode,
} from 'src/generated/graphql';
import { getSelectedProduct } from 'src/utils/cookies';

import HeaderMessage from './components/HeaderMessage/HeaderMessage';
import Package from './components/Package/Package';
import FeatureList from './components/Package/components/DesktopFeatures/components/FeatureList/FeatureList';
import PackageSpacer from './components/PackageSpacer/PackageSpacer';

const PackagesContainer = () => {
  const selectedProduct = getSelectedProduct();
  const location = useLocation();
  const { listingId } = queryString.parseUrl(location.search).query;

  if (selectedProduct && listingId) {
    return (
      <Navigate
        to={queryString.stringifyUrl({
          url: Routes.PAYMENT_CONFIRM,
          query: { listingId, productToken: selectedProduct.toLowerCase() },
        })}
      />
    );
  }

  return <Packages />;
};

const Packages = () => {
  const { loading, data, error } = useQuery(
    GetListingProductsForPackagesPageDocument,
  );

  if (loading) {
    return <CustomLoader />;
  }

  if (error || !data || data.listingProducts.length === 0) {
    return (
      <Notice
        tone="critical"
        data={{ testid: 'get-listing-products-error-notice' }}
      >
        <Text>
          There was an error getting packages. Please try again later.
        </Text>
      </Notice>
    );
  }

  const productsFreemium = [
    {
      ...data.listingProducts.find(
        (lp) => lp.productCode === ListingProductCode.Freemium,
      )!,
      description: 'Free',
    },
    {
      ...data.listingProducts.find(
        (lp) => lp.productCode === ListingProductCode.ListUntilSold,
      )!,
      description: 'Standard',
    },
    data.listingProducts.find(
      (lp) => lp.productCode === ListingProductCode.Accelerate,
    )!,
  ];

  const products = isFreemiumExperiment
    ? productsFreemium
    : [
        data.listingProducts.find(
          (lp) => lp.productCode === ListingProductCode.SixAdPack,
        )!,
        data.listingProducts.find(
          (lp) => lp.productCode === ListingProductCode.ListUntilSold,
        )!,
        data.listingProducts.find(
          (lp) => lp.productCode === ListingProductCode.PayPerEnquiry,
        )!,
      ].filter(hasProductInfo);

  return (
    <Stack space="large">
      <HeaderMessage />
      <Columns
        space={['gutter', 'gutter', 'none']}
        collapseBelow="desktop"
        alignY="bottom"
      >
        <Hidden below="desktop">
          <FeatureList />
        </Hidden>

        <Fragment>
          {products.map((product, i, array) => {
            const isLastProduct = i === array.length - 1;

            return (
              <Fragment key={i}>
                <Package product={product} />
                {!isLastProduct && (
                  <Hidden below="desktop">
                    <PackageSpacer />
                  </Hidden>
                )}
              </Fragment>
            );
          })}
        </Fragment>
      </Columns>
    </Stack>
  );
};

export default PackagesContainer;
